import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  TableSortLabel,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Grid,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  LocationOn as LocationIcon,
  Download as DownloadIcon,
  GetApp as ExcelIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { liveAnalyticsAPI } from '../../services/liveAnalyticsApi';

const Schools: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [orderBy, setOrderBy] = React.useState<string>('school_name');
  const [order, setOrder] = React.useState<'asc' | 'desc'>('asc');
  const [downloadMenuAnchor, setDownloadMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [isDownloading, setIsDownloading] = React.useState(false);

  // Filter states
  const [schoolTypeFilter, setSchoolTypeFilter] = React.useState<string>('');
  const [schoolLevelFilter, setSchoolLevelFilter] = React.useState<string>('');
  const [zoneFilter, setZoneFilter] = React.useState<string>('');
  const [districtFilter, setDistrictFilter] = React.useState<string>('');

  const { data: schoolsData, isLoading } = useQuery({
    queryKey: ['liveSchools', { search: searchTerm, page: page + 1 }],
    queryFn: () => liveAnalyticsAPI.getSchoolsList()
  });

  // Handle both possible response structures - memoized to prevent unnecessary re-renders
  const schools = React.useMemo(() => {
    if (Array.isArray(schoolsData?.data?.data)) {
      return schoolsData!.data.data;
    }
    if (Array.isArray(schoolsData?.data)) {
      return schoolsData!.data;
    }
    return [];
  }, [schoolsData]);

  // Extract unique school types and levels for filter options
  const schoolTypes = React.useMemo(() => {
    const types = new Set<string>();
    schools.forEach((school: any) => {
      if (school.school_type) {
        types.add(school.school_type);
      }
    });
    return Array.from(types).sort();
  }, [schools]);

  const schoolLevels = React.useMemo(() => {
    const levels = new Set<string>();
    schools.forEach((school: any) => {
      if (school.education_level) {
        // Handle comma-separated levels
        const levelList = school.education_level.split(', ');
        levelList.forEach((level: string) => {
          if (level.trim()) {
            levels.add(level.trim());
          }
        });
      }
    });
    return Array.from(levels).sort();
  }, [schools]);

  // Extract unique zones (regions) for filter options
  const zones = React.useMemo(() => {
    const zoneSet = new Set<string>();
    schools.forEach((school: any) => {
      if (school.region?.region_name) {
        zoneSet.add(school.region.region_name);
      }
    });
    return Array.from(zoneSet).sort();
  }, [schools]);

  // Extract unique districts for filter options (filtered by zone if selected)
  const districts = React.useMemo(() => {
    const districtSet = new Set<string>();
    schools.forEach((school: any) => {
      if (school.district?.district_name) {
        // If zone filter is active, only show districts in that zone
        if (!zoneFilter || school.region?.region_name === zoneFilter) {
          districtSet.add(school.district.district_name);
        }
      }
    });
    return Array.from(districtSet).sort();
  }, [schools, zoneFilter]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleSchoolTypeChange = (event: SelectChangeEvent<string>) => {
    setSchoolTypeFilter(event.target.value);
    setPage(0);
  };

  const handleSchoolLevelChange = (event: SelectChangeEvent<string>) => {
    setSchoolLevelFilter(event.target.value);
    setPage(0);
  };

  const handleZoneChange = (event: SelectChangeEvent<string>) => {
    setZoneFilter(event.target.value);
    setDistrictFilter(''); // Reset district when zone changes
    setPage(0);
  };

  const handleDistrictChange = (event: SelectChangeEvent<string>) => {
    setDistrictFilter(event.target.value);
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSchoolTypeFilter('');
    setSchoolLevelFilter('');
    setZoneFilter('');
    setDistrictFilter('');
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Filter and sort schools - memoized to ensure proper re-computation
  const filteredSchools = React.useMemo(() => {
    console.log('Filtering schools:', {
      totalSchools: schools.length,
      schoolTypeFilter,
      schoolLevelFilter,
      zoneFilter,
      districtFilter,
      searchTerm
    });

    const filtered = (schools || []).filter((school: any) => {
      // Search filter - handle empty search term
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm ||
        (school.school_name?.toLowerCase() || '').includes(searchLower) ||
        (school.school_reg_number?.toLowerCase() || '').includes(searchLower) ||
        (school.district?.district_name?.toLowerCase() || '').includes(searchLower);

      // School type filter - exact match
      const matchesType = !schoolTypeFilter || school.school_type === schoolTypeFilter;

      // School level filter - check if the level is in the comma-separated list
      const matchesLevel = !schoolLevelFilter ||
        (school.education_level && school.education_level.split(', ').some(
          (level: string) => level.trim() === schoolLevelFilter
        ));

      // Zone (Region) filter - exact match
      const matchesZone = !zoneFilter || school.region?.region_name === zoneFilter;

      // District filter - exact match
      const matchesDistrict = !districtFilter || school.district?.district_name === districtFilter;

      return matchesSearch && matchesType && matchesLevel && matchesZone && matchesDistrict;
    });

    console.log('Filtered result:', filtered.length, 'schools');

    // Sort the filtered results
    return filtered.sort((a: any, b: any) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      // Handle nested properties
      if (orderBy === 'district_name') {
        aValue = a.district?.district_name || '';
        bValue = b.district?.district_name || '';
      } else if (orderBy === 'region_name') {
        aValue = a.region?.region_name || '';
        bValue = b.region?.region_name || '';
      } else if (orderBy === 'shehia_name') {
        aValue = a.shehia?.shehia_name || '';
        bValue = b.shehia?.shehia_name || '';
      }

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = (bValue || '').toString().toLowerCase();
      }

      if (bValue < aValue) {
        return order === 'asc' ? 1 : -1;
      }
      if (bValue > aValue) {
        return order === 'asc' ? -1 : 1;
      }
      return 0;
    });
  }, [schools, searchTerm, schoolTypeFilter, schoolLevelFilter, zoneFilter, districtFilter, orderBy, order]);

  // Get paginated schools
  const paginatedSchools = filteredSchools.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getStatusChip = (isActive: boolean) => (
    <Chip
      label={isActive ? 'Active' : 'Inactive'}
      color={isActive ? 'success' : 'default'}
      size="small"
    />
  );

  const getLevelColor = (level: string) => {
    const levelLower = level?.toLowerCase() || '';
    if (levelLower.includes('pre-primary') || levelLower.includes('tutu')) return 'secondary';
    if (levelLower.includes('primary') && !levelLower.includes('pre')) return 'primary';
    if (levelLower.includes('secondary')) return 'warning';
    if (levelLower.includes('basic')) return 'info';
    return 'default';
  };

  const handleDownloadClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setDownloadMenuAnchor(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadMenuAnchor(null);
  };

  const downloadFile = async (format: 'excel' | 'csv') => {
    setIsDownloading(true);
    try {
      const params = new URLSearchParams();
      params.append('report_type', 'schools');
      params.append('year', new Date().getFullYear().toString());
      if (searchTerm) params.append('search', searchTerm);
      if (schoolTypeFilter) params.append('school_type', schoolTypeFilter);
      if (schoolLevelFilter) params.append('education_level', schoolLevelFilter);

      const endpoint = format === 'csv'
        ? 'export-schools-csv'
        : 'export-schools-excel';

      console.log(`Starting ${format.toUpperCase()} download...`);

      const response = await fetch(
        `http://localhost:8000/api/v1/sis-integration/${endpoint}/?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }

      const contentType = response.headers.get('content-type');
      const expectedTypes = {
        csv: 'text/csv',
        excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      };

      if (!contentType?.includes(expectedTypes[format])) {
        const text = await response.text();
        console.error('Unexpected response:', text);
        throw new Error('Server returned unexpected response format');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `zanzibar_schools_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'csv'}`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      console.log(`${format.toUpperCase()} download completed successfully`);
      alert(`${format.toUpperCase()} file downloaded successfully!`);
    } catch (error) {
      console.error('Download error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Download failed: ${errorMessage}`);
    } finally {
      setIsDownloading(false);
      handleDownloadClose();
    }
  };

  const hasActiveFilters = searchTerm || schoolTypeFilter || schoolLevelFilter || zoneFilter || districtFilter;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Schools</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Chip
            label={`${filteredSchools.length} schools`}
            color="primary"
            variant="outlined"
          />
          <Button
            variant="outlined"
            startIcon={isDownloading ? <CircularProgress size={20} /> : <DownloadIcon />}
            onClick={handleDownloadClick}
            disabled={isDownloading}
          >
            {isDownloading ? 'Downloading...' : 'Export'}
          </Button>
          <Menu
            anchorEl={downloadMenuAnchor}
            open={Boolean(downloadMenuAnchor)}
            onClose={handleDownloadClose}
          >
            <MenuItem onClick={() => downloadFile('excel')}>
              <ListItemIcon>
                <ExcelIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Download Excel (.xlsx)</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => downloadFile('csv')}>
              <ListItemIcon>
                <DownloadIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Download CSV (.csv)</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Filters Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FilterIcon color="action" />
          <Typography variant="subtitle1" fontWeight="medium">
            Filters
          </Typography>
          {hasActiveFilters && (
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              sx={{ ml: 'auto' }}
            >
              Clear All
            </Button>
          )}
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              placeholder="Search schools..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Zone (Region)</InputLabel>
              <Select
                value={zoneFilter}
                label="Zone (Region)"
                onChange={handleZoneChange}
              >
                <MenuItem value="">
                  <em>All Zones</em>
                </MenuItem>
                {zones.map((zone) => (
                  <MenuItem key={zone} value={zone}>
                    {zone}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>District</InputLabel>
              <Select
                value={districtFilter}
                label="District"
                onChange={handleDistrictChange}
              >
                <MenuItem value="">
                  <em>All Districts</em>
                </MenuItem>
                {districts.map((district) => (
                  <MenuItem key={district} value={district}>
                    {district}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>School Type</InputLabel>
              <Select
                value={schoolTypeFilter}
                label="School Type"
                onChange={handleSchoolTypeChange}
              >
                <MenuItem value="">
                  <em>All Types</em>
                </MenuItem>
                {schoolTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>School Level</InputLabel>
              <Select
                value={schoolLevelFilter}
                label="School Level"
                onChange={handleSchoolLevelChange}
              >
                <MenuItem value="">
                  <em>All Levels</em>
                </MenuItem>
                {schoolLevels.map((level) => (
                  <MenuItem key={level} value={level}>
                    {level}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {searchTerm && (
              <Chip
                label={`Search: "${searchTerm}"`}
                onDelete={() => setSearchTerm('')}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {zoneFilter && (
              <Chip
                label={`Zone: ${zoneFilter}`}
                onDelete={() => { setZoneFilter(''); setDistrictFilter(''); }}
                size="small"
                color="secondary"
                variant="outlined"
              />
            )}
            {districtFilter && (
              <Chip
                label={`District: ${districtFilter}`}
                onDelete={() => setDistrictFilter('')}
                size="small"
                color="default"
                variant="outlined"
              />
            )}
            {schoolTypeFilter && (
              <Chip
                label={`Type: ${schoolTypeFilter}`}
                onDelete={() => setSchoolTypeFilter('')}
                size="small"
                color="info"
                variant="outlined"
              />
            )}
            {schoolLevelFilter && (
              <Chip
                label={`Level: ${schoolLevelFilter}`}
                onDelete={() => setSchoolLevelFilter('')}
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
          </Box>
        )}
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'school_reg_number'}
                  direction={orderBy === 'school_reg_number' ? order : 'asc'}
                  onClick={() => handleRequestSort('school_reg_number')}
                >
                  EMIS ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'school_name'}
                  direction={orderBy === 'school_name' ? order : 'asc'}
                  onClick={() => handleRequestSort('school_name')}
                >
                  School Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'district_name'}
                  direction={orderBy === 'district_name' ? order : 'asc'}
                  onClick={() => handleRequestSort('district_name')}
                >
                  District
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'school_type'}
                  direction={orderBy === 'school_type' ? order : 'asc'}
                  onClick={() => handleRequestSort('school_type')}
                >
                  Type
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'education_level'}
                  direction={orderBy === 'education_level' ? order : 'asc'}
                  onClick={() => handleRequestSort('education_level')}
                >
                  Level
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'current_enrollment'}
                  direction={orderBy === 'current_enrollment' ? order : 'asc'}
                  onClick={() => handleRequestSort('current_enrollment')}
                >
                  Enrollment
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'total_teachers'}
                  direction={orderBy === 'total_teachers' ? order : 'asc'}
                  onClick={() => handleRequestSort('total_teachers')}
                >
                  Teachers
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'total_classes'}
                  direction={orderBy === 'total_classes' ? order : 'asc'}
                  onClick={() => handleRequestSort('total_classes')}
                >
                  Classes
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'total_streams'}
                  direction={orderBy === 'total_streams' ? order : 'asc'}
                  onClick={() => handleRequestSort('total_streams')}
                >
                  Streams
                </TableSortLabel>
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  <CircularProgress size={24} sx={{ mr: 1 }} />
                  Loading schools...
                </TableCell>
              </TableRow>
            ) : paginatedSchools.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  No schools found matching your filters
                </TableCell>
              </TableRow>
            ) : (
              paginatedSchools.map((school: any) => (
                <TableRow key={school.school_id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {school.school_reg_number}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {school.school_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {school.district?.district_name || 'Unknown'}
                      </Typography>
                    </Box>
                    {school.region?.region_name && (
                      <Typography variant="caption" color="text.secondary">
                        {school.region.region_name}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={school.school_type || 'Unknown'}
                      color={school.school_type === 'Government' ? 'success' : 'info'}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={school.education_level || 'Unknown'}
                      color={getLevelColor(school.education_level)}
                      variant="filled"
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight="medium">
                      {school.current_enrollment?.toLocaleString() || 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight="medium">
                      {school.total_teachers?.toLocaleString() || 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight="medium">
                      {school.total_classes || 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight="medium">
                      {school.total_streams || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {getStatusChip(school.status === 1)}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => navigate(`/schools/${school.school_id}`)}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filteredSchools.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Schools per page:"
      />
    </Box>
  );
};

export default Schools;
